import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { OtpInputComponent } from '../widgets/otp-input.component';

@Component({
  selector: 'app-email-confirmation-step',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule, TranslateModule, OtpInputComponent],
  host: { class: 'flex-1 flex flex-col min-h-0' },
  template: `
    <div class="flex flex-col flex-1 min-h-0">
      <div class="flex-1 overflow-auto min-h-0">
        <h2 class="headline-medium text-primary mb-2 hidden md:block" style="font-family: 'DM Sans', sans-serif">{{ 'Confirm your email' | translate }}</h2>
        <p class="body-large text-on-surface-variant mb-6">
          {{ 'We sent a verification code to your email address. Enter the 6-digit code below' | translate }}
        </p>

        <div class="mb-4">
          <app-otp-input
            #otpInput
            [hasError]="hasError"
            (codeComplete)="onCodeComplete($event)"
          ></app-otp-input>
        </div>

        @if (hasError) {
          <p class="text-error body-small mb-4 text-center">{{ 'Incorrect code. Try again' | translate }}</p>
        }

        @if (loading) {
          <div class="flex justify-center mb-4">
            <mat-spinner diameter="24"></mat-spinner>
          </div>
        }

        <p class="body-small text-on-surface-variant mb-6 text-center">
          {{ "Didn't receive a code?" | translate }}
          @if (!resendDisabled) {
            <button mat-button class="!text-primary !p-0 !min-w-0" (click)="onResend()">{{ 'Click here' | translate }}</button>
          } @else {
            <span class="text-on-surface-variant">{{ 'Resend in {{seconds}}s' | translate: { seconds: cooldownSeconds } }}</span>
          }
        </p>
      </div>

      <div class="shrink-0 pt-4">
        <div class="flex items-center justify-between gap-4">
          <div class="hidden md:block w-1/2">
            <button mat-stroked-button type="button" class="w-full" (click)="goBack.emit()">
              {{ 'Go back' | translate }}
            </button>
          </div>
          <button mat-stroked-button type="button" class="flex-1" (click)="remindLater.emit()">
            {{ 'Remind me later' | translate }}
          </button>
        </div>

        <div class="text-center mt-2">
          <button mat-button class="text-on-surface-variant !text-sm" (click)="doNotAskAgain.emit()">
            {{ 'Do not ask me again' | translate }}
          </button>
        </div>
      </div>
    </div>
  `,
})
export class EmailConfirmationStepComponent {
  @ViewChild('otpInput') otpInput!: OtpInputComponent;
  @Input() loading = false;
  @Input() hasError = false;
  @Input() resendDisabled = false;
  @Input() cooldownSeconds = 0;
  @Output() verifyCode = new EventEmitter<string>();
  @Output() resendCode = new EventEmitter<void>();
  @Output() remindLater = new EventEmitter<void>();
  @Output() doNotAskAgain = new EventEmitter<void>();
  @Output() goBack = new EventEmitter<void>();

  onCodeComplete(code: string): void {
    this.verifyCode.emit(code);
  }

  onResend(): void {
    this.resendCode.emit();
  }

  clearOtp(): void {
    this.otpInput?.clear();
  }
}
